import React, { useEffect, useState } from "react";

// import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import "../../assets/CSS/modelform.css";

import StepsCard from "../../components/Cards/StepsCard";
import SelectMaterial from "../../components/ModelForm/SelectMaterial";
import ManageModel from "../../components/ModelForm/ManageModel";
import ModelsAPI from "../../API/models.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import MultistepCompletionHandler from "../../components/Handlers/MultistepCompletionHandler";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import {
    BOOLEAN_DROPDOWN,
    SORTING_DIRECTION_OPTIONS,
} from "../../utils/constants";
import { getMappedParameterLocalizations } from "../../utils/generalMappers";

const CustomModelForm = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [activeUnit, setActiveUnit] = useState([1]);
    const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
        useState(false);
    const [classificationParameters, setClassificationParameters] = useState(
        []
    );
    const [, setStepButtonDisabled] = useState(true);

    const { t } = useTranslation();

    const STEPS = [
        { number: "1", name: t("createdModelStep1") },
        { number: "2", name: t("createdModelStep6") },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => (
                <Trans i18nKey="createCustomModel">Create Custom Model</Trans>
            ),
        },
    ];

    const finalStepTitles = {
        isSubmitting: t("modelCreationSubmittingTitle"),
        isSubmitted: t("modelCreatingSubmitSuccessTitle"),
        default: t("modelCreationSubmitFailTitle"),
    };

    const finalStepContent = {
        isSubmitting: t("modelCreationSubmittingMessage"),
        isSubmitted: t("modelCreationSubmitSuccessMessage"),
        default: (
            <span>
                <Trans i18nKey="modelCreationSubmitFailMessage">
                    Your model could not be uploaded. Please check your internet
                    connection and try again. if you still face any issue please
                    contact us at,
                </Trans>
                <b> Support@si-ware.com</b>
            </span>
        ),
    };

    //Step 1
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    //Step 2
    const [modelInfo, setModelInfo] = useState({});
    const [scannerSettings, setScannerSettings] = useState({
        interpolation: "257 points",
        apodization: "Boxcar",
        FFTPoints: "32k points",
        gain: "Default (0)",
        scanTime: "5000",
    });

    const [userAccess, setUserAccess] = useState([]);
    const [organizationAccess, setOrganizationAccess] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, setIsSubmited] = useState(false);
    const [parametersSettings, setParametersSettings] = useState([]);

    const [modelOptions, setModelOptions] = useState({
        supportedAccessories: [],
        supportedDevices: [],
        enforceOutlierDetection: false,
        isDynamicSorting: false,
        sortingDirection: SORTING_DIRECTION_OPTIONS.ASC,
        isSortingByValues: BOOLEAN_DROPDOWN.true,
        dynamicSortedParameterNames: [],
    });

    useEffect(() => {
        if (currentStep === 1 && selectedMaterial !== null) {
            setStepButtonDisabled(false);
        }
    }, [selectedMaterial]);

    const nextStep = (e, stepNumber) => {
        if (stepNumber) {
            setCurrentStep(stepNumber);
        } else {
            setCurrentStep((prev) => prev + 1);
        }
        setStepButtonDisabled(true);
    };

    const prevStep = (e, stepNumber) => {
        if (stepNumber) {
            setCurrentStep(stepNumber);
        } else {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const mapParametersSettings = () => {
        let mappedParameterSettings = {};

        parametersSettings.forEach((parameter, parameterIndex) => {
            const newParamDetails = [...parameter.parameterDetails];
            const baseUnit = newParamDetails.splice(0, 1);
            const units = newParamDetails.map((conversionUnit, unitIndex) => {
                if (conversionUnit.unit) {
                    return {
                        unitConversionId: conversionUnit.unit.value,
                        decimalPlaces: conversionUnit.decimalPlaces,
                        displayUnit:
                            activeUnit[parameterIndex] - 1 === unitIndex,
                    };
                }
            });
            const classifierId = classificationParameters.find(
                (classificationParam) =>
                    classificationParam.fakeId === parameter.fakeId
            );

            mappedParameterSettings[parameter.name] = {
                classifierId: classifierId?.classifier?.value || null,
                order: parameterIndex,
                unitId: baseUnit[0].unit.value,
                decimalPlaces: baseUnit[0].decimalPlaces,
                modelParameterTranslations: getMappedParameterLocalizations(
                    parameter?.localizations
                ),
                units,
            };
        });
        return mappedParameterSettings;
    };

    const getParameterNames = () => {
        return parametersSettings.map((parameter) => parameter.name);
    };

    const getModelOptions = () => {
        const supportedAccessoryIds = modelOptions?.supportedAccessories.map(
            (a) => a.value
        );
        const supportedDeviceIds = modelOptions?.supportedDevices.map(
            (a) => a.value
        );
        const dynamicSortedParameterNames =
            modelOptions?.dynamicSortedParameterNames.map((a) => a.value);

        return {
            supportedAccessoryIds,
            supportedDeviceIds,
            enforceOutlierDetection: modelOptions?.enforceOutlierDetection,
            isDynamicSorting: modelOptions?.isDynamicSorting,
            sortingDirection: modelOptions?.sortingDirection?.value,
            isSortingByValues: modelOptions?.isSortingByValues?.value,
            dynamicSortedParameterNames,
        };
    };

    const submitModel = () => {
        return new Promise((resolve, reject) => {
            let createModel = {
                modelName: modelInfo.name,
                materialId: selectedMaterial,
                description: modelInfo.description,
                image: modelInfo.image,
                options: modelInfo.options,
                parameterNames: getParameterNames(),
                modelUserAccesses: userAccess,
                modelOrganizationAccesses: organizationAccess,
                parametersSettings: mapParametersSettings(),
                ...getModelOptions(),
                isCustom: true,
                settings: scannerSettings,
            };

            ModelsAPI.createModel(createModel)
                .then(() => {
                    setIsSuccessfullySubmitted(true);
                    SuccessAlert(t("createdModelSuccessMessage"));
                    LogCapture.info(
                        `Custom Model created with name: ${modelInfo?.name}`,
                        ENTITIES.models,
                        ACTIONS.models.createCustomModel
                    );
                    resolve(true);
                })
                .catch((err) => {
                    console.log(err);
                    ErrorAlert(t("createdModelFailMessage"));
                    reject(false);
                    LogCapture.error(
                        `Couldn't create custom model with name: ${modelInfo?.name}, error: ${err}`
                    );
                });
        });
    };

    const renderForm = () => {
        switch (currentStep) {
            case 1:
                return (
                    <SelectMaterial
                        selectedRow={selectedMaterial}
                        setSelectedRow={setSelectedMaterial}
                        nextStepButtonText={t("createdModelStep6")}
                        nextStep={nextStep}
                    />
                );

            case 2:
                return (
                    <ManageModel
                        model={modelInfo}
                        setModel={setModelInfo}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        submitModel={submitModel}
                        setUserAccess={setUserAccess}
                        setOrganizationAccess={setOrganizationAccess}
                        isSubmitting={isSubmitting}
                        setIsSubmitting={setIsSubmitting}
                        setIsSubmited={setIsSubmited}
                        scannerSettings={scannerSettings}
                        setScannerSettings={setScannerSettings}
                        parametersSettings={parametersSettings}
                        setParametersSettings={setParametersSettings}
                        modelOptions={modelOptions}
                        setModelOptions={setModelOptions}
                        isCustom={true}
                        activeUnit={activeUnit}
                        setActiveUnit={setActiveUnit}
                        classificationParameters={classificationParameters}
                        setClassificationParameters={
                            setClassificationParameters
                        }
                    />
                );

            case 3:
                return (
                    <MultistepCompletionHandler
                        prevStep={prevStep}
                        isSubmitted={isSuccessfullySubmitted}
                        isSubmitting={isSubmitting}
                        titles={finalStepTitles}
                        content={finalStepContent}
                        successRedirect={"/models"}
                    />
                );
            default:
                return <div></div>;
        }
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-model-form-header main-content-header flex items-center">
                <i className="fas fa-cog" />
                <h2 className="h2">
                    <Trans i18nKey="addNewModel"> New Model</Trans>
                </h2>
            </header>
            <StepsCard
                steps={STEPS}
                activeNumber={currentStep}
                className="mb-4"
            />

            {renderForm()}
        </>
    );
};

export default CustomModelForm;
